import React, { useState } from 'react';
import { usePortal } from '../../contexts/PortalContext';
import { ContactPortal } from '../ContactPortal';
import { shootDays, shootRange } from '../../utils/clientDates';
import type { Shoot } from '../../types/portal';

/**
 * THE FOOT OF THE PAGE: what happens next, then who to ask.
 *
 * ONE LINE OF SCHEDULE, NOT A SECTION (8 Oct 2026). Tom: *"the schedule
 * might still be going while they've got things to approve."* So the
 * schedule is never a phase the page moves out of — it is always there, and
 * while there is a cut to approve it is always small. One date in a line, and
 * the rest behind a disclosure for the client who wants it. Nothing navigates.
 *
 * **`hideSchedule` is set when the project has a tracker** (9 Oct 2026). The
 * tracker IS the schedule, in more detail and further ahead, so repeating the
 * next date here would put dates in three places on one page — the lead, the
 * tracker and the foot. That is the convolution this page was rebuilt to
 * remove. The contact half always stays.
 *
 * THE CONTACT IS A PERSON, NOT AN ADDRESS. The name and role come from the
 * project's own team, and the button opens the message dialog that already
 * reaches the studio. There is no invented email here: an address nobody
 * checks is worse than none, which is the rule SignOff follows too.
 */
export function PageFoot({
  shoots,
  hideSchedule = false
}: {
  shoots: Shoot[];
  hideSchedule?: boolean;
}) {
  const {
    activeProject,
    clientPreview
  } = usePortal();
  const [open, setOpen] = useState(false);
  const [allDates, setAllDates] = useState(false);
  const next = shoots[0];
  const rest = shoots.slice(1);
  const lead = activeProject.team[0];
  const callSheet = activeProject.callSheet?.url;
  const quiet = 'label border border-rule px-5 py-3.5 text-graphite no-underline transition-colors duration-150 ease-out hover:border-ink hover:text-ink';
  return <>
      {next && !hideSchedule && <div className="mt-16 border-t border-rule pt-8" data-id="element-133" data-mp="3don24f0oex67">
          <div className="flex flex-wrap items-baseline justify-between gap-5" data-id="element-134" data-mp="2ba77f3dal42j">
            <div data-id="element-135" data-mp="2fahll3b8n0sh">
              <p className="label m-0 text-mute" data-id="element-136" data-mp="2fc12mw4pxfvb">Next</p>
              <p className="mt-3 m-0 text-[1.05rem] leading-relaxed" data-id="element-137" data-mp="2f8n27tfm7gqi">
                {[shootRange(next), next.title].filter(Boolean).join(' — ')}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3" data-id="element-138" data-mp="2p2vpa1lvmhha">
              {callSheet && <a href={callSheet} target="_blank" rel="noopener noreferrer" className={quiet} data-id="element-139" data-mp="2tzm12cqneqww">
                  Call sheet
                </a>}
              {rest.length > 0 && <button type="button" onClick={() => setAllDates(shown => !shown)} className={quiet} data-id="element-140" data-mp="331dbtw01pnxm">
                  {allDates ? 'Hide schedule' : 'Full schedule'}
                </button>}
            </div>
          </div>

          {allDates && rest.length > 0 && <ul className="m-0 mt-8 list-none p-0" data-id="element-141" data-mp="162eezx1xnkst">
              {rest.map(shoot => <li key={shoot.id} className="flex flex-wrap items-baseline gap-x-7 gap-y-2 border-t border-rule py-5" data-id="element-142" data-mp="11b4n0psww5dr">
                  <span className="font-display text-xl leading-none sm:text-2xl" data-id="element-143" data-mp="rd8hcdiqu2n1">
                    {shootRange(shoot)}
                  </span>
                  <span className="text-[1rem] text-graphite" data-id="element-144" data-mp="1oznyfe79cya2">
                    {[shoot.title, shootDays(shoot), shoot.location || shoot.city].filter(Boolean).join(' · ')}
                  </span>
                </li>)}
            </ul>}
        </div>}

      <div className="mt-16 border-t border-rule pt-8" data-id="element-145" data-mp="1b3adtw7fggrd">
        <p className="label m-0 text-mute" data-id="element-146" data-mp="3op4dkor11om">Any questions</p>
        {lead ? <>
            <p className="mt-4 m-0 text-[1.05rem] leading-relaxed" data-id="element-147" data-mp="3vqdliap5jp2d">{lead.name}</p>
            <p className="mt-1 m-0 text-[0.95rem] leading-relaxed text-mute" data-id="element-148" data-mp="1ni8fu0wtnfww">{lead.role}</p>
          </> : <p className="mt-4 m-0 text-[1.05rem] leading-relaxed" data-id="element-149" data-mp="1saiec5c05oas">Portal Productions · Dubai</p>}
        <p className="mt-7 m-0" data-id="element-150" data-mp="1dlm0tsr6fj2s">
          <button type="button" onClick={clientPreview ? undefined : () => setOpen(true)} aria-disabled={clientPreview || undefined} className={quiet} data-id="element-151" data-mp="1dihmw3nwt8yx">
            Send a message
          </button>
        </p>
      </div>

      <ContactPortal open={open} onClose={() => setOpen(false)} data-id="element-152" data-mp="2b7t32w00czbz" />
    </>;
}